import { z } from "zod";
import { SETTING } from "../constants.js";
import { notificationDeliverySchema, type NotificationDelivery } from "./notifications.js";

/** Fixed lots trade the same size regardless of stop distance; risk_pct sizes each order
 *  so a full stop-out costs `riskPct` of balance spread across the signal's orders. */
export const lotPolicySchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("fixed"),
    lotsPerOrder: z.number().positive().max(100),
  }),
  z.object({
    mode: z.literal("risk_pct"),
    riskPct: z.number().positive().max(20),
    maxLots: z.number().positive().max(100),
  }),
]);
export type LotPolicy = z.infer<typeof lotPolicySchema>;

/** How far a quote may sit from the signal's stated entry before we treat the message as
 *  describing a different instrument. Metals and indices move in much wider percentages
 *  than FX majors, so the default is overridable per symbol. */
export const priceSanitySchema = z.object({
  defaultPct: z.number().positive().max(50),
  overrides: z.record(z.string(), z.number().positive().max(50)),
});
export type PriceSanity = z.infer<typeof priceSanitySchema>;

/**
 * Symbols whose broker pip is not worth what the default assumes.
 *
 * Only gold is here, and deliberately so. 100 broker pips is $1.00 on this broker's XAUUSD —
 * the one instrument whose spec has actually been measured (`pipSize` 0.01, `contractSize`
 * 100, `docs/spike-findings.md`) — and it is the value the planner's gold tests have used
 * since M6. `price_sanity_pct` can carry a longer list because a percentage is scale-free;
 * pips are not, so a number here for a symbol whose `pipSize` nobody has looked at would be a
 * guess dressed as a setting. Add them from the app once the spec is known.
 */
export const DEFAULT_MARKET_THRESHOLD_OVERRIDES: Readonly<Record<string, number>> = { XAUUSD: 100 };

/**
 * How near the live quote a stated entry has to be before we just take the market.
 *
 * W2.9: this was a single number, and its unit is BROKER pips — which is not a unit of
 * economic distance at all. A pip here is whatever `pipSize` the symbol carries, and this
 * broker's gold pip is 0.01, a tenth of what a channel means when it says "gold moved a pip".
 * Ten of them is ten cents, so "BUY GOLD @ 2400" posted while gold trades at 2400.60 was
 * measured as sixty pips out and rested as a limit the market can simply walk away from,
 * while the same ten is exactly right on EURUSD. Hence a default plus per-symbol overrides,
 * the shape `price_sanity_pct` already uses.
 *
 * A bare number is accepted and lifted, because that is what every database written before
 * this change holds. Rejecting it would fall back to the registry default on read and change
 * how trades are placed at deploy time, silently.
 *
 * The lift carries the seeded overrides with it. A stored number was written when per-symbol
 * values did not exist, so it cannot be expressing a preference about gold — reading it as
 * "10 everywhere, including gold" would leave every existing database with exactly the bug
 * this fixes, and `seedDefaults` cannot repair it (`onConflictDoNothing` never touches a row
 * that is already there). An explicitly stored object is honoured exactly as written.
 */
export const marketThresholdSchema = z.preprocess(
  (value) =>
    typeof value === "number"
      ? { defaultPips: value, overrides: { ...DEFAULT_MARKET_THRESHOLD_OVERRIDES } }
      : value,
  z.object({
    defaultPips: z.number().positive().max(10_000),
    overrides: z.record(z.string(), z.number().positive().max(10_000)),
  }),
);
export type MarketThreshold = { defaultPips: number; overrides: Record<string, number> };

/**
 * The threshold in force for one broker symbol.
 *
 * Keyed on the broker's own symbol name, which is what every call site has in hand from the
 * spec. This broker decorates nothing (measured: plain `XAUUSD`, `EURUSD`), so an exact match
 * is honest here; a broker that appended a suffix would need its own key rather than a
 * prefix-matching guess, and the setting is editable from the app for exactly that reason.
 */
export function marketThresholdFor(config: MarketThreshold, brokerSymbol: string): number {
  return config.overrides[brokerSymbol] ?? config.defaultPips;
}

export const weekendScheduleSchema = z.object({
  enabled: z.boolean(),
  /** MetaApi bills while an account is deployed and FX is closed all weekend. */
  undeployCron: z.string().min(9),
  redeployCron: z.string().min(9),
  tz: z.string().min(1),
});
export type WeekendSchedule = z.infer<typeof weekendScheduleSchema>;

export const modelsSchema = z.object({
  /** Extraction only — cheap, must support vision for chart screenshots. */
  parser: z.string().min(1),
  /** Decides what to actually do with full DB and account context. */
  decider: z.string().min(1),
  /**
   * Reasoning effort for the decider. The parser stays at default: extraction does not
   * benefit from deliberation, and it sits on the ingestion path where latency matters.
   *
   * M36 added "none", which sends no reasoning option at all. Now that any model on
   * OpenRouter can be typed into the field above, a model with no reasoning parameter is
   * one dropdown change away — and asking it to accept one is a refusal, not a downgrade.
   * Widening the enum is backward-compatible: a stored "high" still parses.
   */
  deciderEffort: z.enum(["none", "low", "medium", "high"]).default("high"),
});
export type Models = z.infer<typeof modelsSchema>;

/**
 * Limits on everything open at once, rather than on any single trade.
 *
 * The gap this closes is the one the previous attempt's manifest promised and never
 * implemented: every per-trade check can pass ten times in an hour and leave ten times the
 * intended risk on the account. Both limits are settings rather than constants because the
 * right number depends on the account and on how many channels are enabled — and because a
 * limit you cannot raise from your phone at 3am is one you will eventually route around.
 */
/**
 * M15 — informational credit thresholds, in USD. Nothing acts on them: crossing warn gets
 * a ⚠ in the daily briefing and the UI, crossing alarm a 🚨. Zero disables a threshold.
 *
 * M36d renamed the model-provider pair from `openai*` to `llm*`, since which vendor is
 * behind them is now a setting. The stored row is rewritten by migration rather than read
 * both ways: `getSetting` catches a validation failure, logs it, and falls back to the
 * registry default, so a row left in the old shape would quietly revert Andy's tuned
 * thresholds to 5/2 with one log line to say so.
 */
export const creditThresholdsSchema = z.object({
  metaapiWarnUsd: z.number().min(0).max(10_000),
  metaapiAlarmUsd: z.number().min(0).max(10_000),
  llmWarnUsd: z.number().min(0).max(10_000),
  llmAlarmUsd: z.number().min(0).max(10_000),
});
export type CreditThresholds = z.infer<typeof creditThresholdsSchema>;

/**
 * M15 — the only honest way to show "remaining OpenAI credits": OpenAI exposes no balance
 * API, so Andy types the dashboard figure after each top-up and the app subtracts its own
 * metered spend since that moment. Null means never set, rendered as a placeholder rather
 * than a guess.
 */
export const openaiCreditBaselineSchema = z.object({
  amountUsd: z.number().min(0).max(100_000).nullable(),
  /** ISO timestamp of when `amountUsd` was read off the dashboard. */
  asOf: z.iso.datetime({ offset: true }).nullable(),
});
export type OpenaiCreditBaseline = z.infer<typeof openaiCreditBaselineSchema>;

export const exposureCapsSchema = z.object({
  /** Signals with at least one order still live. Zero disables the count limit. */
  maxOpenSignals: z.number().int().min(0).max(100),
  /**
   * Total money at risk across every live order — each order's distance to its own stop —
   * as a percentage of equity. Zero disables the risk limit.
   */
  maxTotalRiskPct: z.number().min(0).max(100),
});
export type ExposureCaps = z.infer<typeof exposureCapsSchema>;

const confidence = z.number().min(0).max(1);

/**
 * Every app_settings row, its schema and its seeded default. The API's
 * `PUT /api/settings/:key` validates against this registry, so an unknown key or a
 * malformed value is rejected before it can reach the trade engine.
 */
export const SETTINGS_REGISTRY = {
  [SETTING.killSwitch]: {
    schema: z.boolean(),
    default: false as boolean,
    description: "Blocks every trade mutation while true. Nothing overrides it.",
  },
  [SETTING.dryRun]: {
    schema: z.boolean(),
    default: true as boolean,
    description: "Simulates fills instead of calling MetaApi. Starts on until validated.",
  },
  [SETTING.lotPolicy]: {
    schema: lotPolicySchema,
    default: { mode: "fixed", lotsPerOrder: 0.01 } satisfies LotPolicy,
    description: "Position size per order. Channels may override.",
  },
  [SETTING.marketThresholdPips]: {
    schema: marketThresholdSchema,
    default: {
      defaultPips: 10,
      overrides: { ...DEFAULT_MARKET_THRESHOLD_OVERRIDES },
    } satisfies MarketThreshold,
    description:
      "Entry within this many pips of the live quote executes at market; further away becomes a pending order. Per-symbol, because a pip is worth a different amount on each.",
  },
  [SETTING.priceSanityPct]: {
    schema: priceSanitySchema,
    default: {
      defaultPct: 0.5,
      overrides: { XAUUSD: 2, XAGUSD: 2, BTCUSD: 5, US30: 2, NAS100: 2, SPX500: 2 },
    } satisfies PriceSanity,
    description: "Max deviation between a signal's prices and the live quote before we skip it.",
  },
  [SETTING.parseConfidenceMin]: {
    schema: confidence,
    default: 0.6,
    description: "Below this the parser output is discarded and you are notified.",
  },
  [SETTING.decideConfidenceMin]: {
    schema: confidence,
    default: 0.7,
    description: "Below this the signal waits for your approval instead of executing.",
  },
  /**
   * M20 — the age ceiling on trading a signal unattended.
   *
   * Until now nothing anywhere asked how old a message was before trading it: the decider
   * prompt carried `nowIso` but never the post time, so a backfilled signal from last week
   * read exactly like one posted a minute ago. Price sanity was the only thing standing in
   * the way, and it passes precisely in the dangerous case — a dead setup whose level the
   * market has since wandered back to.
   *
   * Deliberately tight by default. A false refusal costs one missed trade; a false accept
   * opens a position on a setup nobody is watching any more. Two hours covers a restart, a
   * catch-up sweep and a short outage, and is one edit away here for anyone who wants a
   * longer leash. It bounds the automatic path only — approving from the app is a human
   * saying "yes, still valid", and that overrides it.
   */
  [SETTING.signalMaxAgeMinutes]: {
    schema: z.number().int().min(1).max(10_080),
    default: 120,
    description:
      "How old a signal's message may be and still be traded automatically. Older ones are skipped, not judged. Approving from the app overrides this.",
  },
  [SETTING.weekendSchedule]: {
    schema: weekendScheduleSchema,
    default: {
      enabled: false,
      undeployCron: "15 22 * * 5",
      redeployCron: "45 20 * * 0",
      tz: "UTC",
    } satisfies WeekendSchedule,
    description: "Undeploys the MetaApi account over the weekend to stop billing.",
  },
  [SETTING.exposureCaps]: {
    schema: exposureCapsSchema,
    default: { maxOpenSignals: 5, maxTotalRiskPct: 6 } satisfies ExposureCaps,
    description:
      "Refuses a new trade once this many signals are live, or once total risk-to-stop would exceed this share of equity.",
  },
  [SETTING.dailyLlmBudgetUsd]: {
    /**
     * Zero disables the cap, matching `exposure_caps`' own convention.
     *
     * Enforced against the summed `decisions.cost_usd` for the UTC day. Calls on a model
     * absent from the pricing table record a null cost and are therefore unbudgetable — that
     * gap is deliberate (a wrong cost that looks right is worse than an absent one) and is
     * written down in `docs/hardening-audit-2026-08-10.md` rather than papered over.
     */
    schema: z.number().min(0).max(10_000),
    default: 10,
    description:
      "Stops parsing and deciding once the day's model spend reaches this many dollars. 0 disables the cap.",
  },
  [SETTING.models]: {
    schema: modelsSchema,
    default: { parser: "gpt-5.6-luna", decider: "gpt-5.6-terra", deciderEffort: "high" } satisfies Models,
    description: "Models used by the parser and decider stages.",
  },
  [SETTING.creditThresholds]: {
    schema: creditThresholdsSchema,
    default: {
      metaapiWarnUsd: 5,
      metaapiAlarmUsd: 2,
      llmWarnUsd: 5,
      llmAlarmUsd: 2,
    } satisfies CreditThresholds,
    description:
      "Remaining-credit levels that mark the daily briefing and the dashboard with ⚠ (warn) or 🚨 (alarm). Informational only; nothing is paused. 0 disables a threshold.",
  },
  [SETTING.openaiCreditBaseline]: {
    schema: openaiCreditBaselineSchema,
    default: { amountUsd: null, asOf: null } satisfies OpenaiCreditBaseline,
    description:
      "Your OpenAI balance as read off their dashboard, and when. Remaining = this minus metered spend since; unset shows a placeholder (OpenAI has no balance API).",
  },
  /**
   * M23c — which notification kinds are allowed out, per delivery arm.
   *
   * Empty by default, and empty means "every kind behaves as it always has": the resolver
   * (`deliveryFor`) supplies telegram-on for anything absent. So this setting can only ever
   * be the record of a decision Andy made, never the reason something went quiet on its own.
   *
   * Consequently a malformed stored value is harmless in the right direction — `getSetting`
   * logs it and falls back to `{}`, which sends everything. On a live-money notifier that is
   * the only acceptable direction for a config failure to fail in.
   */
  [SETTING.notificationDelivery]: {
    schema: notificationDeliverySchema,
    default: {} satisfies NotificationDelivery,
    description:
      "Per-kind delivery. A kind that is not listed uses its default: Telegram on, push only for the things that mean something is wrong.",
  },
} as const;

export type SettingsRegistry = typeof SETTINGS_REGISTRY;
export type KnownSettingKey = keyof SettingsRegistry;

export function isKnownSettingKey(key: string): key is KnownSettingKey {
  return Object.hasOwn(SETTINGS_REGISTRY, key);
}

/** Throws if the value does not match the registered schema for that key. */
export function parseSettingValue<K extends KnownSettingKey>(
  key: K,
  value: unknown,
): z.infer<SettingsRegistry[K]["schema"]> {
  return SETTINGS_REGISTRY[key].schema.parse(value) as z.infer<SettingsRegistry[K]["schema"]>;
}
